import React from "react";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ReportColumn } from "./types";
import { useReportBuilderStore } from "./store";
import { AGGREGATION_LABELS, DATA_TYPE_CONFIG } from "./constants";
import { FormulaEditor } from "./FormulaEditor";

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

// ─── Labeled field ────────────────────────────────────────────────────────────

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

// ─── Column editor panel ──────────────────────────────────────────────────────

export function ColumnEditor() {
  const {
    config,
    activeSheetId,
    activeColumnId,
    setActiveColumn,
    updateColumn,
    isAdvancedMode,
    isEditorCollapsed,
    toggleEditorCollapsed,
    toggleGroupBy,
    addSortRule,
    removeSortRule,
  } = useReportBuilderStore();

  const activeSheet =
    config.sheets.find((s) => s.id === activeSheetId) ?? config.sheets[0];
  const col = activeSheet.columns.find((c) => c.id === activeColumnId);

  // In simple mode collapse the panel when no column is selected
  if (!isAdvancedMode && !col) {
    return (
      <div className="w-10 bg-white border-l border-gray-200 flex flex-col items-center py-3 gap-2">
        <div
          className="text-[10px] text-gray-400 font-semibold"
          style={{ writingMode: "vertical-rl" }}
        >
          EDITOR
        </div>
      </div>
    );
  }

  if (isEditorCollapsed) {
    return (
      <div className="w-10 bg-white border-l border-gray-200 flex flex-col items-center py-3 gap-2">
        <button
          onClick={toggleEditorCollapsed}
          title="Expand editor"
          className="p-1.5 rounded hover:bg-gray-100 transition-colors"
        >
          <ChevronDownIcon className="w-4 h-4 text-gray-500 -rotate-90" />
        </button>
        <div
          className="text-[10px] text-gray-400 font-semibold"
          style={{ writingMode: "vertical-rl" }}
        >
          EDITOR
        </div>
      </div>
    );
  }

  if (!col) {
    return (
      <div className="w-72 flex-shrink-0 bg-white border-l border-gray-200 flex flex-col">
        {/* Header */}
        <div className="px-3 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Column Settings
          </h2>
          <button
            onClick={toggleEditorCollapsed}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
            title="Collapse"
          >
            <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400 rotate-90" />
          </button>
        </div>

        {/* Empty / guide state */}
        <div className="flex-1 flex flex-col px-4 py-6 gap-4 overflow-y-auto">
          <div className="flex flex-col items-center text-center gap-2 pb-4 border-b border-gray-100">
            <div className="w-10 h-10 rounded-full bg-activeColor flex items-center justify-center">
              <svg
                className="w-5 h-5 text-inputBorderFocus"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <p className="text-xs font-semibold text-gray-600">
              Select a column to edit
            </p>
            <p className="text-[11px] text-gray-400 leading-relaxed">
              Click any column card on the canvas to rename it, change the
              format, or configure aggregation.
            </p>
          </div>

          {/* Quick how-to steps */}
          <div className="flex flex-col gap-3">
            {[
              {
                icon: "mdi:table-column-plus-after",
                step: "1",
                text: "Add columns from the schema panel on the left — click + or drag onto the canvas.",
              },
              {
                icon: "mdi:swap-horizontal",
                step: "2",
                text: "Drag column cards left or right to reorder them. The order here is the column order in the export.",
              },
              {
                icon: "mdi:filter-variant",
                step: "3",
                text: "Switch to Advanced mode to add filters, sort, and group-by rules.",
              },
              {
                icon: "mdi:content-save",
                step: "4",
                text: "Save the report, then use Export → Generate Report to queue it for download.",
              },
            ].map(({ icon, step, text }) => (
              <div key={step} className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-activeColor flex items-center justify-center text-[10px] font-bold text-primary flex-shrink-0 mt-0.5">
                  {step}
                </span>
                <p className="text-[11px] text-gray-500 leading-snug">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const cfg = DATA_TYPE_CONFIG[col.type];
  const isGrouped = activeSheet.groupBy.includes(col.id);
  const sortRule = activeSheet.sortBy.find((s) => s.column === col.id);
  const isNumeric = ["number", "currency", "percentage"].includes(col.type);

  return (
    <div className="w-72 flex-shrink-0 bg-white border-l border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="px-3 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}
            >
              {col.isCustom ? "ƒ" : cfg.label}
            </span>
            <h2 className="text-xs font-semibold text-gray-700 truncate max-w-[160px]">
              {col.displayName}
            </h2>
          </div>
          <button
            onClick={() => setActiveColumn(null)}
            className="text-gray-300 hover:text-gray-500 transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
        {!col.isCustom && col.sourceTable && (
          <div className="text-[10px] text-gray-400 font-mono">
            {col.sourceTable}.{col.sourceColumn}
          </div>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {/* ── What this column means ── always visible so users know what they added ── */}
        {col.description && col.description !== col.displayName && (
          <div className="mb-4 px-2.5 py-2 bg-activeColor border border-inputBorder rounded-lg">
            <p className="text-[10px] font-semibold text-primary uppercase tracking-wide mb-0.5">
              About this column
            </p>
            <p className="text-[11px] text-primaryText leading-snug">
              {col.description}
            </p>
          </div>
        )}

        {/* ── Display ── */}
        <Section title="Display">
          <Field label="Display name">
            <input
              type="text"
              value={col.displayName}
              onChange={(e) =>
                updateColumn(col.id, { displayName: e.target.value })
              }
              className="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
          </Field>

          {isAdvancedMode && (
            <Field label="Description">
              <textarea
                value={col.description ?? ""}
                onChange={(e) =>
                  updateColumn(col.id, { description: e.target.value })
                }
                rows={2}
                placeholder="Describe what this column represents..."
                className="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary resize-none"
              />
            </Field>
          )}

          {isAdvancedMode && !col.isCustom && (
            <Field label="Number format">
              <select
                value={col.format ?? ""}
                onChange={(e) =>
                  updateColumn(col.id, {
                    format: e.target.value || null,
                  })
                }
                className="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary bg-white"
              >
                <option value="">Default</option>
                {isNumeric && (
                  <>
                    <option value="comma">1,234.56</option>
                    <option value="currency_usd">$1,234.56</option>
                    <option value="percent">42.3%</option>
                    <option value="scientific">1.23e+3</option>
                  </>
                )}
                {col.type === "date" && (
                  <>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    <option value="relative">Relative (2 days ago)</option>
                  </>
                )}
              </select>
            </Field>
          )}
        </Section>

        {/* ── Formula (custom columns only) ── */}
        {col.isCustom && (
          <Section title="Formula">
            <Field label="Data type">
              <select
                value={col.type}
                onChange={(e) =>
                  updateColumn(col.id, {
                    type: e.target.value as ReportColumn["type"],
                  })
                }
                className="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary bg-white mb-2"
              >
                {Object.entries(DATA_TYPE_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.label === "ƒ" ? "Custom" : v.label}
                  </option>
                ))}
              </select>
            </Field>
            <FormulaEditor
              value={col.formula ?? ""}
              onChange={(v) => updateColumn(col.id, { formula: v })}
              columnId={col.id}
            />
          </Section>
        )}

        {/* ── Aggregation (advanced mode only, numeric columns) ── */}
        {isAdvancedMode && isNumeric && (
          <Section title="Aggregation">
            <Field label="Function">
              <select
                value={col.aggregation ?? ""}
                onChange={(e) =>
                  updateColumn(col.id, {
                    aggregation:
                      (e.target.value as ReportColumn["aggregation"]) || null,
                  })
                }
                className="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary bg-white"
              >
                <option value="">None</option>
                {Object.entries(AGGREGATION_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
          </Section>
        )}

        {/* ── Sorting & Grouping (advanced mode) ── */}
        {isAdvancedMode && (
          <Section title="Sorting & Grouping">
            {/* Group By toggle */}
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs text-gray-600">
                Group by this column
              </label>
              <button
                onClick={() => toggleGroupBy(col.id)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  isGrouped ? "bg-primary" : "bg-gray-200"
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                    isGrouped ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>

            {/* Sort */}
            <div className="flex items-center justify-between">
              <label className="text-xs text-gray-600">Sort</label>
              <div className="flex items-center gap-1">
                {["asc", "desc"].map((dir) => (
                  <button
                    key={dir}
                    onClick={() => {
                      if (sortRule?.direction === dir) {
                        removeSortRule(col.id);
                      } else {
                        addSortRule({
                          column: col.id,
                          direction: dir as "asc" | "desc",
                        });
                      }
                    }}
                    className={`px-2 py-1 text-[11px] border rounded transition-colors ${
                      sortRule?.direction === dir
                        ? "bg-activeColor border-inputBorderFocus text-secondary font-medium"
                        : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    {dir === "asc" ? "↑ ASC" : "↓ DESC"}
                  </button>
                ))}
                {sortRule && (
                  <button
                    onClick={() => removeSortRule(col.id)}
                    className="text-gray-300 hover:text-gray-500"
                    title="Remove sort"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}
